import { Card, type CardProps, minmax, repeat } from "@hopper-ui/components";
import { type DetailedHTMLProps, type HTMLAttributes, type ReactElement, useCallback } from "react";
import { type GridListItemRenderProps } from "react-aria-components";
import { GridList, GridListItem, type GridListItemProps, type GridListProps } from "../grid-list/GridList.tsx";

type GridCardListProps<T> = GridListProps<T>;

export const GridCardList = <T extends object>(props: GridCardListProps<T>) => {
    const { children, ...otherProps } = props;

    return (
        <GridList
            display="grid"
            gap="inline-md"
            gridTemplateColumns={repeat("auto-fill", minmax("320px", "1fr"))}
            layout="grid"
            {...otherProps}
        >
            {children}
        </GridList>
    );
};

interface GridCardListItemRenderProps extends GridListItemRenderProps {
    variant?: CardProps["variant"];
    elevation?: CardProps["elevation"];
}

function renderBaseCardRenderFunction(
    props: DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
    { isDisabled, selectionMode, isSelected, variant, elevation }: GridCardListItemRenderProps
) {
    // we extract color since it's incompatible with Card's color
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { color, ...rest } = props;

    const isInteractive = !isDisabled && (selectionMode !== "none" || props.onClick);

    return isInteractive ? (
        <InteractiveCard isSelected={isSelected} elevation={elevation} variant={variant} {...rest} />
    ) : (
        <Card variant={variant} elevation={elevation} {...rest} />
    );
}

interface InteractiveCardProps extends CardProps {
    isSelected: boolean;
}

// TODO: Rebrand: This should be a Tile if Tile was a proper component
const InteractiveCard = ({ isSelected, ...rest }: InteractiveCardProps) => {
    const extraProps = {
        border: isSelected ? "primary" : undefined,
        backgroundColor: isSelected ? "primary-weak" : undefined
    } satisfies Partial<CardProps>;

    return <Card borderHover="neutral-hover" borderActive="neutral-press" cursor="pointer" {...extraProps} {...rest} />;
};

export interface GridCardListItemProps extends Omit<GridListItemProps, "render"> {
    variant?: CardProps["variant"];
    elevation?: CardProps["elevation"];
    render?: GridCardListItemRenderFunction;
}

type GridCardListItemDefaultRenderer = (
    props: DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
    renderProps: GridCardListItemRenderProps
) => ReactElement;
export type GridCardListItemRenderFunction = (
    props: DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
    renderProps: GridCardListItemRenderProps,
    baseRenderer: GridCardListItemDefaultRenderer
) => ReactElement;

export const GridCardListItem = ({ children, variant, elevation, render, ...rest }: GridCardListItemProps) => {
    const renderFn: GridCardListItemDefaultRenderer = useCallback(
        (props, renderProps) => {
            if (!render) {
                return renderBaseCardRenderFunction(props, { ...renderProps, variant, elevation });
            } else {
                return render(props, { ...renderProps, variant, elevation }, renderBaseCardRenderFunction);
            }
        },
        [variant, elevation, render]
    );

    return (
        <GridListItem
            display="flex"
            flexDirection="row"
            padding="inset-lg"
            outline="none"
            outlineFocus="primary"
            // TODO: Rebrand: Wont be needed after the rebrand, SG card have this rounded-lg by default
            borderRadius="rounded-lg"
            render={renderFn}
            {...rest}
        >
            {children}
        </GridListItem>
    );
};
